<template>
  <div class="image-with-products">
    <div v-if="video" class="image-with-products__video">
      <video-bunny
        :src="`https://iframe.mediadelivery.net/embed/${
          BUNNY_STREAM_FOLDERS[video.folder]
        }/${video.id}?autoplay=true&loop=true&muted=true`"
      />
    </div>

    <lazy-image
      v-if="video === null && imageMobile && imageMobile.image"
      :image="imageMobile.image"
      :widths="imageMobile.widths"
      :sizes="imageMobile.sizes"
      provider="sanity"
      :class="imageDesktop && imageDesktop.image ? 'd-md-none' : ''"
      :preload="imageMobile.isFirst"
      :force-show="imageMobile.isFirst"
    />

    <lazy-image
      v-if="video === null && imageDesktop && imageDesktop.image"
      :image="imageDesktop.image"
      :widths="imageDesktop.widths"
      :sizes="imageDesktop.sizes"
      provider="sanity"
      :class="imageMobile && imageMobile.image ? 'd-none d-md-block' : ''"
      :preload="imageDesktop.isFirst"
      :force-show="imageDesktop.isFirst"
    />

    <div
      v-if="products.length > 0"
      class="list"
      :class="`${position(productsPosition)} ${
        products.length > 1 ? '' : 'list--single'
      }`"
    >
      <button-link
        v-for="product in products"
        :key="product.uid"
        :to="{
          path: $contextSanityPath(product),
          query:
            products.length === 1 && targetColor ? { color: targetColor } : {},
        }"
        as="link"
        with-icon
        icon="arrow-right"
        icon-position="right"
        animate-hover
        :class="products.length > 1 ? 'multi-products' : 'single-product'"
      >
        <span v-if="products.length > 1">
          {{ product.title }} -
          {{ $n(product.shopify_product?.priceRange?.minVariantPrice) }}&euro;
        </span>
        <div
          v-else
          :class="{
            'd-flex align-center': products.length > 1 || !targetColor,
            'd-md-flex align-center': products.length === 1 && targetColor,
          }"
        >
          <div
            class="product-name d-flex flex-wrap d-md-block align-baseline"
            :class="{
              'pr-1 mr-1': products.length > 1 || !targetColor,
              'product-name--color mb-0-4 mb-md-0 pr-md-1 mr-md-1':
                products.length === 1 && targetColor,
              'product-name--hide-prices': hideProductPrices,
            }"
          >
            <p
              v-if="productRender !== 'only-color'"
              class="mb-0 lh-1 text-no-wrap"
              :class="{
                'mr-0-6 mr-md-0':
                  targetColor !== null && productRender === 'default',
              }"
            >
              {{ product.title }}
            </p>
            <p
              v-if="
                targetColor !== null && productRender !== 'only-product-name'
              "
              class="mb-0 lh-1 text-no-wrap"
              :class="{
                'text-big mt-md-0-2': productRender === 'default',
              }"
            >
              {{ colorName }}
            </p>
          </div>

          <div
            v-if="!hideProductPrices"
            class="d-flex align-baseline pt-0-2 pt-md-0"
          >
            <p
              class="price d-flex flex-wrap align-baseline text-secondary lh-1 mb-0 mr-0-6 mr-md-1"
            >
              <span class="number pr-0-4"
                >{{
                  $n(product.shopify_product?.priceRange?.minVariantPrice)
                }}&euro;</span
              >
              <span class="text-small text-no-wrap">{{
                $t(
                  product.shopify_product?.priceRange?.minVariantPrice !==
                    product.shopify_product?.compareAtPriceRange
                      ?.minVariantPrice
                    ? 'product.in_preco'
                    : 'product.in_stock'
                )
              }}</span>
            </p>

            <p
              v-if="
                product.shopify_product?.priceRange?.minVariantPrice !==
                product.shopify_product?.compareAtPriceRange?.minVariantPrice
              "
              class="price compare d-flex flex-wrap align-baseline text-secondary lh-1 mb-0"
            >
              <span class="number pr-0-4"
                >{{
                  $n(
                    product.shopify_product?.compareAtPriceRange
                      ?.minVariantPrice
                  )
                }}&euro;</span
              >
              <span class="text-small text-no-wrap">{{
                $t('product.in_stock')
              }}</span>
            </p>
          </div>
        </div>
      </button-link>
    </div>
  </div>
</template>

<script>
import LazyImage from '@/components/Image/LazyImage'
import VideoBunny from '@/components/video/bunny'
import ButtonLink from '@/components/buttons/Link'
import { BUNNY_STREAM_FOLDERS } from '~/const'

export default {
  name: 'ImageWithProducts',
  components: {
    VideoBunny,
    LazyImage,
    ButtonLink,
  },
  props: {
    imageDesktop: {
      type: Object,
      required: true,
    },
    imageMobile: {
      type: Object,
      default: () => null,
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    video: {
      type: Object,
      default: () => null,
    },
    products: {
      type: Array,
      default: () => [],
    },
    productsPosition: {
      type: String,
      default: 'bottom',
    },
    targetColor: {
      type: String,
      default: null,
    },
    hideProductPrices: {
      type: Boolean,
      default: false,
    },
    productRender: {
      type: String,
      default: 'default',
    },
  },
  data: () => ({
    BUNNY_STREAM_FOLDERS,
  }),
  computed: {
    colorName() {
      if (this.targetColor) {
        const p = this.products[0]

        if (p && p.shopify_product && p.shopify_product.options) {
          const color = p.shopify_product.colorOptions.find(
            (o) => o._key === this.targetColor
          )

          if (color) {
            return color.name
          }
        }
      }

      return ''
    },
  },
  methods: {
    position(position) {
      return `list--${position}`
    },
  },
}
</script>

<style lang="scss">
.image-with-products {
  position: relative;
  overflow: hidden;

  img {
    will-change: transform;
    transition: transform 0.5s var(--asphalte-animation-function);
    width: calc(100% + 1px);
    height: calc(100% + 1px);
  }

  &__video {
    position: absolute;
    top: 50%;
    left: 50%;
    pointer-events: none;
    transform: translate(-50%, -50%);
    width: 150vw;
    height: 100%;
  }

  .list {
    position: absolute;
    display: flex;
    flex-direction: column;
    padding: calc(var(--spacing));
    gap: calc(var(--spacing) * 0.6);
    inset: 0;

    &--single {
      padding: 0;
    }

    .btn-link {
      font-size: var(--h5-font-size);
      font-family: var(--secondary-font-family);
      color: var(--tertiary-color);
      text-shadow: 0 0 3px rgba(#381800, 0.5);
      font-weight: 600;

      .icon {
        width: #{rem(20px)};
        height: #{rem(20px)};
      }

      &:first-child {
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 1;
        }
      }

      &:not(:first-child) {
        position: relative;
        z-index: 2;
      }

      &.single-product {
        align-self: stretch;
        justify-content: space-between;
        padding: var(--spacing) var(--spacing) calc(var(--spacing) * 0.8);
        transition: all 0.5s var(--asphalte-animation-function);

        .product-name {
          border-right: 1px solid currentcolor;

          &.product-name--color {
            @include mq($until: tablet) {
              border-right: 0;
            }
          }

          &.product-name--hide-prices {
            border-right: 0;
          }
        }

        .price {
          .number {
            font-size: var(--color-price-font-size);
            line-height: 1;
          }
        }

        .compare {
          opacity: 0.8;

          @include mq($from: tablet) {
            opacity: 0.6;
          }

          .number {
            text-decoration: line-through;
          }
        }

        > span::after {
          content: none;
        }
      }
    }

    .link--underline > span::after {
      bottom: 0;
    }

    &--top {
      justify-content: flex-end;
    }

    &--bottom {
      justify-content: flex-end;
    }
  }

  &:has(.list) {
    @include on-hover-and-focus {
      .lazy-image img {
        transform: scale(1.1);
        transition: transform 1s var(--asphalte-animation-function);
      }

      .btn-link {
        background-color: var(--tertiary-color);
        color: var(--secondary-color);
        text-shadow: 0 0 0 rgba(#381800, 0);
      }
    }
  }

  .grid-of-products:not(.grid-of-products--full-images) & {
    .lazy-image-container {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      height: 100%;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
